/**
 * Translates the keyboard into the shared Command (DESIGN §5), using a
 * configurable keymap so two humans can share one keyboard for versus play.
 * Dash, extreme dash and special are edge-triggered (one fire per key press).
 */
import Phaser from 'phaser';
import type { Command, Controller } from '../game/command.ts';
import { normalize } from '../game/vec.ts';

export interface KeyMap {
  up: number;
  down: number;
  left: number;
  right: number;
  dash: number;
  extreme: number;
  holdA: number;
  holdB: number;
  special: number;
}

const K = Phaser.Input.Keyboard.KeyCodes;

/** Player 1: arrows + Space / Shift / C(Ctrl) / E. */
export const KEYS_P1: KeyMap = {
  up: K.UP,
  down: K.DOWN,
  left: K.LEFT,
  right: K.RIGHT,
  dash: K.SPACE,
  extreme: K.SHIFT,
  holdA: K.C,
  holdB: K.CTRL,
  special: K.E,
};

/** Player 2: WASD + F / T / V / R. */
export const KEYS_P2: KeyMap = {
  up: K.W,
  down: K.S,
  left: K.A,
  right: K.D,
  dash: K.F,
  extreme: K.T,
  holdA: K.V,
  holdB: K.V,
  special: K.R,
};

export class PlayerController implements Controller {
  private readonly keys: Record<keyof KeyMap, Phaser.Input.Keyboard.Key>;
  private prevDash = false;
  private prevExtreme = false;
  private prevSpecial = false;

  constructor(scene: Phaser.Scene, map: KeyMap = KEYS_P1) {
    this.keys = scene.input.keyboard!.addKeys(map) as Record<
      keyof KeyMap,
      Phaser.Input.Keyboard.Key
    >;
  }

  decide(): Command {
    const k = this.keys;
    let x = 0;
    let y = 0;
    if (k.left.isDown) x -= 1;
    if (k.right.isDown) x += 1;
    if (k.up.isDown) y -= 1;
    if (k.down.isDown) y += 1;
    const steer = x === 0 && y === 0 ? { x: 0, y: 0 } : normalize({ x, y });

    const dashDown = k.dash.isDown;
    const extremeDown = k.extreme.isDown;
    const specialDown = k.special.isDown;
    const dash = dashDown && !this.prevDash;
    const extremeDash = extremeDown && !this.prevExtreme;
    const special = specialDown && !this.prevSpecial;
    this.prevDash = dashDown;
    this.prevExtreme = extremeDown;
    this.prevSpecial = specialDown;

    const holdCenter = k.holdA.isDown || k.holdB.isDown;

    return { steer, dash, extremeDash, holdCenter, special };
  }
}
